import * as tf from "@tensorflow/tfjs"

export {
  getLatentCode,
  getRandomLatentVector,
  getInterpolations,
  generateImage,
  getCoordinates,
  ModelInfo
}

let prevLatentNoise = null
const MAX_PIXELS_IN_PARALLEL = 8192

const ModelInfo = [
  {
    include: false,
    internal: "wikiart",
    pretty: "Art"
  },
  {
    include: true,
    internal: "wikiart_portraits",
    pretty: "Paintings"
  },
  {
    include: false,
    internal: "celeb",
    pretty: "Celebrity Faces"
  },
  {
    include: true,
    internal: "celeb_hq",
    pretty: "Celebrity Faces"
  },
  {
    include: true,
    internal: "celeb128",
    pretty: "Celebrity Faces (Larger)"
  },
  {
    include: true,
    internal: "celeb_cppn",
    pretty: "Celebrity Faces (Abstract)"
  },
  {
    include: false,
    internal: "celeb_hq_smaller",
    pretty: "Celebrity Faces"
  },
  {
    include: false,
    internal: "celeb_hq_new",
    pretty: "Celebrity Faces #2"
  },
  {
    include: false,
    internal: "cifar10",
    pretty: "[Abstract] Objects"
  },
  {
    include: true,
    internal: "flowers",
    pretty: "Flowers"
  },
  {
    include: true,
    internal: "cifar10_cars",
    pretty: "Cars"
  },
  {
    include: true,
    internal: "mnist",
    pretty: "Numbers"
  }
]

const LoadedModels = ModelInfo.map(model => model.internal).reduce(
  (acc, curr) => ({ ...acc, [curr]: undefined }),
  {}
)

const ModelConstants = {
  LATENT_NOISE_SIZE: 128,
  CATEGORICAL_SIZE: 10,
  CONTINUOUS_INTERVAL: 1
}

const getLatentCode = (categorical, firstContinuous, secondContinuous) => {
  const latentCode = tf.tidy(() => {
    const categoricalVariable = tf
      .oneHot(categorical, ModelConstants.CATEGORICAL_SIZE)
      .asType("float32")
    const continuousVariables = tf
      .tensor([firstContinuous, secondContinuous])
      .asType("float32")
    const concat = tf
      .concat([categoricalVariable, continuousVariables])
      .reshape([1, -1])
    return concat
  })
  return latentCode
}

const getRandomLatentVector = concat => {
  const res = tf.tidy(() => {
    const latentNoise = tf.randomUniform(
      [1, ModelConstants.LATENT_NOISE_SIZE],
      -1,
      1
    )
    const categoricalVariable = tf
      .oneHot(
        Math.floor(Math.random() * ModelConstants.CATEGORICAL_SIZE),
        ModelConstants.CATEGORICAL_SIZE
      )
      .reshape([1, -1])
      .asType("float32")
    const continuousVariables = tf
      .randomUniform(
        [1, 2],
        -ModelConstants.CONTINUOUS_INTERVAL,
        ModelConstants.CONTINUOUS_INTERVAL
      )
      .asType("float32")
    const latentCode = tf
      .concat([categoricalVariable, continuousVariables], 1)
      .reshape([1, -1])
    if (concat) {
      return tf.concat([latentNoise, latentCode], 1)
    }
    return { latentNoise, latentCode }
  })
  return res
}

const getInterpolations = (z1, z2, frames, includeEnd = false) => {
  const numInterpolations = frames
  let interpolations = []
  for (let i = 0; i <= numInterpolations; i++) {
    if (!includeEnd && i === numInterpolations) {
      break
    }
    const interpolation = tf.tidy(() => {
      const alpha = tf.scalar(i / numInterpolations)
      const _alpha = tf.sub(tf.scalar(1), alpha)
      const z = tf.add(tf.mul(_alpha, z1), tf.mul(alpha, z2))
      return z
    })
    interpolations.push(interpolation)
  }
  return interpolations
}

const loadModel = async modelName => {
  if (!LoadedModels[modelName]) {
    const BASE_URL =
      window.location.protocol === "http:"
        ? window.location.hostname === "localhost"
          ? "http://localhost:8000"
          : `http://${process.env.VUE_APP_LOCAL_IP}:8000`
        : `https://${window.location.hostname}`
    const MODEL_URL = `${BASE_URL}/models/${modelName}/model.json`
    LoadedModels[modelName] = await tf.loadGraphModel(MODEL_URL)
  }
  return LoadedModels[modelName]
}

const getBatchInputs = (imgDim, scale) => {
  const { x, y, r } = getCoordinates(imgDim, scale, 1)
  const batchSizes = new Array(
    Math.floor((imgDim * imgDim) / MAX_PIXELS_IN_PARALLEL)
  )
    .fill(MAX_PIXELS_IN_PARALLEL)
    .concat((imgDim * imgDim) % MAX_PIXELS_IN_PARALLEL)
  const batch = {
    x: tf.split(x, batchSizes, 1),
    y: tf.split(y, batchSizes, 1),
    r: tf.split(r, batchSizes, 1),
    numPixels: batchSizes
  }
  return batch
}

const generateImage = async (modelName, configuration) => {
  const {
    imgDim,
    firstCategory,
    firstContinuous,
    secondContinuous,
    fixedLatentNoise,
    latentVector = null,
    scale
  } = configuration
  const numChannels = modelName !== "mnist" ? 3 : 1
  const model = await loadModel(modelName)
  const unflattened = tf.tidy(() => {
    let latentCode,
      latentNoise = null
    if (latentVector) {
      latentNoise = latentVector.slice(
        [0, 0],
        [-1, ModelConstants.LATENT_NOISE_SIZE]
      )
      latentCode = latentVector.slice(
        [0, ModelConstants.LATENT_NOISE_SIZE],
        [-1, -1]
      )
      if (modelName === "celeb_cppn") {
        latentCode = tf.concat([latentCode, tf.zeros([1, 3])], 1)
      }
    } else {
      //const { latentNoise, latentCode } = getRandomLatentVector(false)
      latentNoise =
        prevLatentNoise && fixedLatentNoise
          ? prevLatentNoise.clone()
          : tf.randomUniform([1, ModelConstants.LATENT_NOISE_SIZE], -1, 1)
      //prevLatentNoise = tf.keep(latentNoise)

      const categoricalVariable = tf
        .oneHot(firstCategory, ModelConstants.CATEGORICAL_SIZE)
        .asType("float32")
      const continuousVariables = tf
        .tensor([firstContinuous, secondContinuous])
        .asType("float32")
      latentCode = tf
        .concat([categoricalVariable, continuousVariables])
        .reshape([1, -1])
      if (modelName === "celeb_cppn") {
        latentCode = tf.concat([latentCode, tf.zeros([1, 3])], 1)
      }
    }

    const batch = getBatchInputs(imgDim, scale)

    let results = []
    for (let i = 0; i < batch.numPixels.length; i++) {
      const inputs = {
        latent_noise: latentNoise,
        latent_codes: latentCode,
        cppn_x: batch.x[i],
        cppn_y: batch.y[i],
        cppn_r: batch.r[i],
        num_pixels: tf.scalar(batch.numPixels[i], "int32")
      }
      const batchRes = model.predict(inputs)
      results.push(batchRes)
    }

    let result

    if (modelName === "celeb_cppn") {
      result = tf.concat(results.map(([_, arr]) => arr), 1)
    } else {
      result = tf.concat(results, 1)
    }

    const unflattened = tf
      .mul(tf.add(tf.reshape(result, [imgDim, imgDim, numChannels]), 1), 127.5)
      .asType("int32")

    if (prevLatentNoise) {
      prevLatentNoise.dispose()
    }
    prevLatentNoise = tf.keep(latentNoise)

    return unflattened
  })

  const img = await tf.browser.toPixels(unflattened)
  const canvasImage = new ImageData(img, imgDim, imgDim)
  tf.dispose([img, unflattened])
  window.console.log("Number of tensors", tf.memory().numTensors)
  return canvasImage
}

const getCoordinates = (imgDim, scale, repeat) => {
  let x = tf
    .tile(tf.linspace(-scale, scale, imgDim), [imgDim])
    .reshape([-1, imgDim])
  let y = x.transpose()

  x = x.reshape([1, -1, 1]).tile([repeat, 1, 1])
  y = y.reshape([1, -1, 1]).tile([repeat, 1, 1])
  const r = tf.sqrt(tf.add(x.square(), y.square()))
  return { x, y, r }
}
